import type { PartialBlock } from '@blocknote/core'

export type BlockNoteJsonContent = PartialBlock[]

type Fetcher = <T>(path: string, init?: RequestInit) => Promise<T>

export interface FileRecord {
  id: string
  workspaceId: string
  parentId: string | null
  projectId: string | null
  name: string
  originalName: string
  contentType: string
  sizeBytes: number
  fileType: 'folder' | 'upload' | 'doc'
  status: 'pending' | 'ready' | 'archived'
  uploadedBy: string | null
  createdAt: string
  updatedAt: string
}

export interface UploadRequest {
  name: string
  contentType: string
  sizeBytes: number
  parentId?: string | null
  projectId?: string | null
}

export interface UploadResponse {
  file: FileRecord
  uploadUrl: string
}

function fileQuery(params?: { parentId?: string | null; projectId?: string | null; includeArchived?: boolean }) {
  const query = new URLSearchParams()
  if (params?.parentId) query.set('parentId', params.parentId)
  if (params?.projectId) query.set('projectId', params.projectId)
  if (params?.includeArchived) query.set('includeArchived', 'true')
  const value = query.toString()
  return value ? `?${value}` : ''
}

export const filesApi = {
  list(fetcher: Fetcher, workspaceSlug: string, params?: { parentId?: string | null; projectId?: string | null; includeArchived?: boolean }) {
    return fetcher<FileRecord[]>(`/api/v1/workspaces/${workspaceSlug}/files${fileQuery(params)}`)
  },

  createFolder(fetcher: Fetcher, workspaceSlug: string, data: { name: string; parentId?: string | null; projectId?: string | null }) {
    return fetcher<FileRecord>(`/api/v1/workspaces/${workspaceSlug}/files/folders`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  requestUpload(fetcher: Fetcher, workspaceSlug: string, data: UploadRequest) {
    return fetcher<UploadResponse>(`/api/v1/workspaces/${workspaceSlug}/files/upload-url`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  completeUpload(fetcher: Fetcher, workspaceSlug: string, fileId: string) {
    return fetcher<FileRecord>(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/complete`, { method: 'POST' })
  },

  uploadBinary(fetcher: Fetcher, workspaceSlug: string, fileId: string, file: File) {
    return fetcher<FileRecord>(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/upload`, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    })
  },

  getDownloadUrl(fetcher: Fetcher, workspaceSlug: string, fileId: string) {
    return fetcher<{ url: string }>(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/download-url`)
  },

  getPreviewUrl(fetcher: Fetcher, workspaceSlug: string, fileId: string) {
    return fetcher<{ url: string }>(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/preview-url`)
  },

  update(fetcher: Fetcher, workspaceSlug: string, fileId: string, patch: { name?: string; parentId?: string | null }) {
    return fetcher<FileRecord>(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },

  archive(fetcher: Fetcher, workspaceSlug: string, fileId: string) {
    return fetcher(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/archive`, { method: 'POST' })
  },

  remove(fetcher: Fetcher, workspaceSlug: string, fileId: string) {
    return fetcher(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}`, { method: 'DELETE' })
  },

  createDoc(fetcher: Fetcher, workspaceSlug: string, data: { name: string; parentId?: string | null; projectId?: string | null; content?: BlockNoteJsonContent }) {
    return fetcher<FileRecord>(`/api/v1/workspaces/${workspaceSlug}/files/docs`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  getContent(fetcher: Fetcher, workspaceSlug: string, fileId: string) {
    return fetcher<{ content: BlockNoteJsonContent }>(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/content`)
  },

  saveContent(fetcher: Fetcher, workspaceSlug: string, fileId: string, content: BlockNoteJsonContent) {
    return fetcher(`/api/v1/workspaces/${workspaceSlug}/files/${fileId}/content`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    })
  },
}
